import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { QUILL_PRINT_CONTENT_CSS } from 'components/QuillEditor/quillContentStyles';

const DEFAULT_PRINTABLE_WIDTH = '140mm';

export default function BasePrintModalA5({
  open,
  onClose,
  onAfterPrint,
  children,
  title,
  pageMargin = '4mm',
  printableWidth = DEFAULT_PRINTABLE_WIDTH
}) {
  const componentRef = useRef();
  const hasPrintedRef = useRef(false);
  const [isReadyToPrint, setIsReadyToPrint] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: title || 'PrintDocument',
    pageStyle: `
      @page { size: A5 portrait; margin: ${pageMargin}; }
      html, body {
        width: ${printableWidth};
        margin: 0;
        font-family: Arial, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      ${QUILL_PRINT_CONTENT_CSS}
    `,
    onAfterPrint: () => {
      onClose();
      onAfterPrint?.();
    }
  });

  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        setIsReadyToPrint(true);
      }, 300);
      return () => clearTimeout(timeout);
    }
    setIsReadyToPrint(false);
    hasPrintedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (isReadyToPrint && !hasPrintedRef.current && open) {
      hasPrintedRef.current = true;
      handlePrint();
    }
  }, [isReadyToPrint, open, handlePrint]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handlePrint}><PrintIcon /></IconButton>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box
        ref={componentRef}
        sx={{
          width: '100%',
          maxWidth: printableWidth,
          margin: '0 auto',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '@media print': {
            padding: 0,
            margin: 0,
            width: `${printableWidth} !important`,
            minWidth: `${printableWidth} !important`,
            maxWidth: `${printableWidth} !important`,
            alignItems: 'flex-start',
            overflow: 'visible'
          },
          '& hr, & .MuiDivider-root': {
            borderColor: '#000',
            backgroundColor: '#000'
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'visible',
            '& *': {
              boxSizing: 'border-box',
              overflow: 'visible'
            },
            '@media print': {
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              overflow: 'visible'
            }
          }}
        >
          {React.cloneElement(children, { handlePrint: handlePrint, onClose: onClose })}
        </Box>
      </Box>
    </Dialog>
  );
}

BasePrintModalA5.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAfterPrint: PropTypes.func,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  pageMargin: PropTypes.string,
  printableWidth: PropTypes.string
};